import { Injectable } from '@angular/core';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { BusinessUnitModel, IBusinessUnitModel } from './business-unit.model';
import {
    BusinessUnitParameterModel,
    IAccessRightFilterCollection,
    ILightWeightBusinessUnit,
    ILightWeightObjectCollection,
} from '@fi-shared/models';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { AccessRightType, ApplicationSettingsType, ObjectTypes } from '@fi-shared/types';
import { IItemsListDataFetchResult } from '@fi-shared/fi-items-list/fi-items-list';
import { BusinessUnitViewDialogComponent } from '@fi-app/business-unit/business-unit-view-dialog/business-unit-view-dialog.component';
import { FiItemsDeleteHelperService } from '@fi-shared/fi-shared-services/fi-items-delete-helper.service';
import { Observable, Subject } from 'rxjs';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { IUserViewDialogData } from '@fi-app/user/user';
import { IBusinessUnitViewDialogData } from '@fi-app/business-unit/business-unit';
import { AccessRightService } from '@fi-shared/fi-shared-services';
import { DataFieldService } from '@fi-app/data-field/data-field.service';

export interface IBusinessUnitAccessData {
    hasEditAccess: boolean;
    isCentralAdmin: boolean;
}

@Injectable()
export class BusinessUnitService {
    constructor(
        private fiBackend: FiBackendService,
        private fiLoading: LoadingService,
        private accessToApplicationTypeService: AccessToApplicationTypeService,
        private fiItemsDeleteHelperService: FiItemsDeleteHelperService,
        private loadingService: LoadingService,
        private accessRightService: AccessRightService,
        private dataFieldService: DataFieldService,
        private fiDialogsService: FiDialogsService,
    ) {}

    deleteBusinessUnits(businessUnitIdsToDelete: number[]) {
        const promise = this.fiLoading.trackPromise(
            this.fiBackend.fetchDataPost('Core/BusinessUnit/DeleteBusinessUnits', businessUnitIdsToDelete),
        );
        return promise;
    }

    getBusinessUnit(id: number) {
        const promise = new Promise<BusinessUnitModel>((resolve, reject) => {
            const businessUnit = new BusinessUnitModel();

            if (id > 0) {
                this.fiBackend
                    .fetchData<IBusinessUnitModel>(`core/BusinessUnit/GetBusinessUnit/${id}`)
                    .then((response) => {
                        businessUnit.Initialize(response);

                        businessUnit.InitializeContacts(response);

                        businessUnit.InitializeAddresses(response);

                        resolve(businessUnit);
                    }, reject);
            } else {
                businessUnit.InitializeContacts(null);
                businessUnit.InitializeAddresses(null);
                resolve(businessUnit);
            }
        });

        return promise;
    }

    getBusinessUnitsListData(parameter: BusinessUnitParameterModel) {
        return this.fiBackend.fetchDataPost<IItemsListDataFetchResult<IBusinessUnitModel>>(
            'Core/BusinessUnit/GetBusinessUnitsListData',
            parameter,
        );
    }

    getLightWeightObjectsForLocalSearch(businessUnitParameterApiModel: BusinessUnitParameterModel) {
        return this.fiBackend.fetchDataPost<ILightWeightObjectCollection>(
            'Core/BusinessUnit/GetLightWeightObjectsForLocalSearch',
            businessUnitParameterApiModel,
        );
    }

    saveBusinessUnit(businessUnitApiModel) {
        return this.fiBackend.fetchDataPost('Core/BusinessUnit/SaveBusinessUnit', businessUnitApiModel);
    }

    getBusinessUnitAccessData(
        businessUnitId: number,
        accessRightFilterCollection: IAccessRightFilterCollection,
    ): IBusinessUnitAccessData {
        if (
            this.accessToApplicationTypeService.HasAccessToApplicationType(
                ApplicationSettingsType.CoreLocal,
                AccessRightType.Write,
            )
        ) {
            const businessUnitIds = accessRightFilterCollection.AccessRightCoreFilter.BusinessUnitIds;
            return {
                hasEditAccess: businessUnitIds.indexOf(businessUnitId) !== -1,
                isCentralAdmin: true,
            };
        }

        /*
        if (this.accessToApplicationTypeService.HasAccessToApplicationType(ApplicationSettingsType.CoreLocal, AccessRightType.Write)) {
            const businessUnitIds = this.userDataService.userData.BusinessUnits.map(bu => bu.Id);
            return {
                hasEditAccess: businessUnitIds.indexOf(businessUnitId) !== -1,
                isCentralAdmin: false,
            }
        }
*/
        return {
            hasEditAccess: false,
            isCentralAdmin: false,
        };
    }

    private prepareBusinessUnitViewDialogData(businessUnitId: number): Promise<IBusinessUnitViewDialogData> {
        const data: IBusinessUnitViewDialogData = {};
        const resultPromise = new Promise<IUserViewDialogData>((resolve, reject) => {
            Promise.all([
                this.getBusinessUnit(businessUnitId).then((result) => {
                    data.businessUnit = result;
                }),
                this.accessRightService.GetAccessRightsForUser().then((result) => {
                    data.accessRightFilterCollection = result;
                }),
                this.dataFieldService.getUnitFields(businessUnitId).then((result) => {
                    data.businessUnitDataFields = result;
                }),
            ]).then(() => {
                const businessUnitAccessData = this.getBusinessUnitAccessData(
                    businessUnitId,
                    data.accessRightFilterCollection,
                );
                // https://chainformation.atlassian.net/browse/FI-4284
                // A user with only Local write rights should be able to edit units they belong to - but they should not be able to: delete, deactivate or re-activate any units.
                data.hasEditAccess = businessUnitAccessData.hasEditAccess;
                data.hasDeleteAccess = businessUnitAccessData.hasEditAccess && businessUnitAccessData.isCentralAdmin;

                resolve(data);
            }, reject);
        });
        return this.loadingService.trackPromise(resultPromise);
    }

    openBusinessUnitViewDialog(businessUnitId: number) {
        this.prepareBusinessUnitViewDialogData(businessUnitId).then((businessUnitViewDialogData) => {
            this.fiDialogsService
                .custom({
                    contentComponentRef: BusinessUnitViewDialogComponent,
                    contentComponentData: businessUnitViewDialogData,
                    hideHeader: true,
                    hideFooter: true,
                    hideDialogTopBorder: true,
                    config: {
                        panelClass: 'fi-business-unit-view-dialog-panel',
                        width: '844px',
                        maxWidth: 'unset',
                    },
                })
                .afterClosed((result) => {
                    if (result.value?.businessUnit) {
                        this.fiItemsDeleteHelperService
                            .commonDeleteConfirm(ObjectTypes.BusinessUnit, [result.value.businessUnit])
                            .then(() => {
                                this.deleteBusinessUnits([businessUnitId]).then(() => {
                                    this.fiItemsDeleteHelperService.showDeletedObjectSimpleSnackBar(
                                        ObjectTypes.BusinessUnit,
                                        [result.value.businessUnit.Id],
                                        result.value.businessUnit.Name,
                                    );
                                    this.removeBusinessUnitFromList$.next(businessUnitId);
                                });
                            });
                    }
                });
        });
    }

    private removeBusinessUnitFromList$ = new Subject<number>();
    get onRemoveBusinessUnitFromList$(): Observable<number> {
        return this.removeBusinessUnitFromList$.asObservable();
    }
}
